import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll, fmtINR } from "@/components/common";

export default function AdminRates() {
  const [customers, setCustomers] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ customer_id: "", rate: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/rate-overrides").then((r) => setOverrides(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  usePoll(load, 10000, []);

  const search = (val) => {
    setQ(val);
    if (val) api.get(`/admin/customers?q=${encodeURIComponent(val)}`).then((r) => setCustomers(r.data)).catch(() => {});
    else setCustomers([]);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/rate-overrides", form); toast.success("Rate override set"); setForm({ customer_id: "", rate: "", reason: "" }); setQ(""); setCustomers([]); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (cid) => { await api.delete(`/admin/rate-overrides/${cid}`); toast.success("Removed"); load(); };

  const selected = customers.find((c) => c.id === form.customer_id);

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Customer Rate Overrides</h1>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        Global default rate: <span className="font-mono font-bold">{fmtINR(settings.global_rate)}</span> / USDT. Overrides take precedence for that customer; orders lock the rate at creation.
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <label className="block"><span className="text-sm font-medium text-slate-700">Search customer</span>
          <input value={q} onChange={(e) => search(e.target.value)} placeholder="name or email" data-testid="rate-customer-search"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" /></label>
        {customers.length > 0 && (
          <div className="border border-slate-200 rounded-xl divide-y max-h-48 overflow-y-auto">
            {customers.map((c) => (
              <button type="button" key={c.id} onClick={() => setForm({ ...form, customer_id: c.id })}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${form.customer_id === c.id ? "bg-blue-50" : ""}`} data-testid={`rate-pick-${c.email}`}>
                {c.name} · {c.email}
              </button>
            ))}
          </div>
        )}
        {selected && <div className="text-sm text-slate-600">Selected: <strong>{selected.name}</strong></div>}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-700">New rate (INR/USDT)</span>
            <input type="number" step="0.01" required value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })}
              data-testid="rate-value-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 font-mono outline-none" /></label>
          <label className="block"><span className="text-sm font-medium text-slate-700">Reason</span>
            <input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="rate-reason-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" /></label>
        </div>
        <button disabled={busy || !form.customer_id} data-testid="set-rate-button" className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">Set override</button>
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-heading font-semibold text-slate-900 mb-3">Active overrides</h2>
        {overrides.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">None.</div> : (
          <div className="space-y-2" data-testid="rate-overrides-list">
            {overrides.map((o) => (
              <div key={o.customer_id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                <div><div className="font-medium text-slate-800 text-sm">{o.customer_name}</div><div className="text-xs text-slate-500">{o.customer_email} · {o.reason}</div></div>
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-blue-700">{fmtINR(o.rate)}</span>
                  <button onClick={() => remove(o.customer_id)} className="text-xs text-rose-600 hover:underline">Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
